import React, { useState, useEffect, useMemo } from 'react';
import { 
  Chart as ChartJS, 
  CategoryScale, 
  LinearScale, 
  BarElement, 
  Title, 
  Tooltip, 
  Legend, 
  ArcElement,
  PointElement,
  LineElement
} from 'chart.js';
import { Bar, Pie } from 'react-chartjs-2';
import saleService from '../services/saleService';
import platformService from '../services/platformService';
import { formatEUR } from '../lib/utils';
import { generateWeeks, isDayInWeek } from '../utils/dashboardWeeks';

// Registrar componentes de Chart.js
ChartJS.register(
  CategoryScale,
  LinearScale,
  BarElement,
  Title,
  Tooltip,
  Legend,
  ArcElement,
  PointElement,
  LineElement
);

const chartPalette = {
  primaryFill: 'rgba(18, 59, 99, 0.72)',
  primaryLine: '#123B63',
  secondaryFill: 'rgba(198, 146, 20, 0.58)',
  secondaryLine: '#C69214',
  successFill: 'rgba(31, 122, 90, 0.74)',
  successLine: '#1F7A5A',
  warningFill: 'rgba(194, 122, 10, 0.74)',
  warningLine: '#C27A0A',
  errorFill: 'rgba(180, 35, 24, 0.72)',
  errorLine: '#B42318',
  fallbackFill: 'rgba(91, 103, 122, 0.34)',
  fallbackLine: '#5B677A',
};

const DashboardScreen = () => {
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [stats, setStats] = useState(null);
  const [commissionStats, setCommissionStats] = useState(null); 
  const [platforms, setPlatforms] = useState([]);
  
  // Filtros aplicados (los que se usan para hacer las peticiones)
  const [filters, setFilters] = useState({
    timeRange: 'thisYear',
    platformId: '',
    status: ['approved', 'pending'],
    startDate: '',
    endDate: '',
    conversionFilter: 'all'
  });
  
  // Filtros temporales (los que se muestran en la interfaz antes de aplicarlos)
  const [tempFilters, setTempFilters] = useState({
    timeRange: 'thisYear',
    platformId: '',
    status: ['approved', 'pending'],
    startDate: '',
    endDate: '',
    conversionFilter: 'all'
  });

  // Función para calcular el rango de fechas según el filtro seleccionado
  const getWeekBounds = (baseDate) => {
    const referenceDate = new Date(baseDate);
    const dayOfWeek = referenceDate.getDay();
    const daysSinceMonday = dayOfWeek === 0 ? 6 : dayOfWeek - 1;

    const monday = new Date(referenceDate);
    monday.setDate(referenceDate.getDate() - daysSinceMonday);

    const sunday = new Date(monday);
    sunday.setDate(monday.getDate() + 6);

    return { monday, sunday };
  };

  const formatDateKey = (date) => {
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const day = String(date.getDate()).padStart(2, '0');
    return `${year}-${month}-${day}`;
  };

  const getMonthKey = (date) => {
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, '0');
    return `${year}-${month}`;
  };

  const monthKeyToDate = (monthKey) => {
    const [year, month] = monthKey.split('-').map(Number);
    return new Date(year, month - 1, 1);
  };

  const formatMonthLabel = (date) => {
    return date.toLocaleDateString('es-ES', { month: 'long', year: 'numeric' });
  };

  const getTodayDateAtMidnight = () => {
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    return today;
  };

  const clampDateRangeEndToToday = (dateRange) => {
    const startDate = new Date(dateRange.startDate);
    const endDate = new Date(dateRange.endDate);
    const today = getTodayDateAtMidnight();

    startDate.setHours(0, 0, 0, 0);
    endDate.setHours(0, 0, 0, 0);

    const clampedEndDate = endDate > today ? today : endDate;

    if (startDate > clampedEndDate) {
      return null;
    }

    return {
      startDate: formatDateKey(startDate),
      endDate: formatDateKey(clampedEndDate)
    };
  };

  const calculateDateRange = (timeRange, customStartDate, customEndDate) => {
    // Crear fechas en la zona horaria de Madrid
    const now = new Date();
    let endDate = new Date(now);
    let startDate = new Date(now);

    if (timeRange === 'custom' && customStartDate && customEndDate) {
      return {
        startDate: customStartDate,
        endDate: customEndDate
      };
    }

    switch (timeRange) {
      case 'lastWeek':
        {
          const { monday: currentWeekMonday } = getWeekBounds(endDate);
          startDate = new Date(currentWeekMonday);
          startDate.setDate(currentWeekMonday.getDate() - 7);
          endDate = new Date(currentWeekMonday);
          endDate.setDate(currentWeekMonday.getDate() - 1);
        }
        break;
      case 'lastMonth':
        startDate = new Date(endDate);
        startDate.setMonth(endDate.getMonth() - 1);
        startDate.setDate(1);
        // Obtener el último día del mes anterior
        endDate = new Date(endDate.getFullYear(), endDate.getMonth(), 0);
        break;
      case 'lastYear':
        startDate = new Date(endDate);
        startDate.setFullYear(endDate.getFullYear() - 1);
        startDate.setMonth(0);
        startDate.setDate(1);
        endDate = new Date(endDate);
        endDate.setFullYear(endDate.getFullYear() - 1);
        endDate.setMonth(11);
        endDate.setDate(31);
        break;
      case 'last7days':
        startDate.setDate(endDate.getDate() - 6);
        break;
      case 'last30days':
        startDate.setDate(endDate.getDate() - 29);
        break;
      case 'last90days':
        startDate.setDate(endDate.getDate() - 89);
        break;
      case 'thisWeek':
        {
          const { monday, sunday } = getWeekBounds(endDate);
          startDate = monday;
          endDate = sunday;
        }
        break;
      case 'thisMonth':
        startDate = new Date(endDate.getFullYear(), endDate.getMonth(), 1);
        // Obtener el último día del mes actual
        endDate = new Date(endDate.getFullYear(), endDate.getMonth() + 1, 0);
        break;
      case 'thisYear':
        startDate = new Date(endDate.getFullYear(), 0, 1);
        // Obtener el último día del año actual
        endDate = new Date(endDate.getFullYear(), 11, 31);
        break;
      case 'currentYear':
        startDate = new Date(endDate.getFullYear(), 0, 1);
        // Obtener el último día del año actual
        endDate = new Date(endDate.getFullYear(), 11, 31);
        break;
      case 'currentMonth':
        startDate = new Date(endDate.getFullYear(), endDate.getMonth(), 1);
        // Obtener el último día del mes actual
        endDate = new Date(endDate.getFullYear(), endDate.getMonth() + 1, 0);
        break;
      default:
        // Por defecto usar el año actual
        startDate = new Date(endDate.getFullYear(), 0, 1);
        endDate = new Date(endDate.getFullYear(), 11, 31);
        break;
    }

    // Ajustar las horas para el inicio y fin del día
    startDate.setHours(0, 0, 0, 0);
    endDate.setHours(23, 59, 59, 999);

    console.log("Rango de fechas calculado:", startDate, endDate);

    const result = {
      startDate: formatDateKey(startDate),
      endDate: formatDateKey(endDate)
    };

    console.log('Fechas calculadas:', result);

    return result;
  };

  // Cargar plataformas
  useEffect(() => {
    const loadPlatforms = async () => {
      try {
        setLoading(true);
        setError('');
        
        // Obtener plataformas para el filtro
        const platformsResponse = await platformService.getActivePlatforms();
        setPlatforms(platformsResponse);
      } catch (err) {
        console.error('Error al cargar plataformas:', err);
        setError('Error al cargar las plataformas');
      }
    };
    
    loadPlatforms();
  }, []);

  // Cargar datos con los filtros actuales
  useEffect(() => {
    const fetchData = async () => {
      try {
        setLoading(true);
        setError(null);
        
        // Calcular fechas según el rango seleccionado
        const dateRange = calculateDateRange(
          filters.timeRange, 
          filters.startDate, 
          filters.endDate
        );

        console.log("Fechas calculadas:", dateRange);
        
        // Preparar parámetros para la API de estadísticas
        const statsParams = {
          startDate: dateRange.startDate,
          endDate: dateRange.endDate,
          status: filters.status,
          conversionFilter: filters.conversionFilter
        };
        
        if (filters.platformId) {
          statsParams.platformId = filters.platformId;
        }
        
        // Obtener estadísticas
        const statsResponse = await saleService.getSalesStats(statsParams);
        setStats(statsResponse);

        // Obtener estadísticas de comisiones
        const commissionResponse = await saleService.getCommissionStats({
          startDate: dateRange.startDate,
          endDate: dateRange.endDate,
          platformId: filters.platformId
        });
        setCommissionStats(commissionResponse);

      } catch (err) {
        console.error('Error al cargar datos:', err);
        setError('Error al cargar los datos. Por favor, intenta nuevamente.');
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, [filters]);
  
  // Carga inicial de datos
  useEffect(() => {
    // Inicializar los filtros con las fechas calculadas
    const dateRange = calculateDateRange('thisMonth');
    setFilters(prevFilters => ({
      ...prevFilters,
      startDate: dateRange.startDate,
      endDate: dateRange.endDate
    }));
    setTempFilters(prevFilters => ({
      ...prevFilters,
      startDate: dateRange.startDate,
      endDate: dateRange.endDate
    }));
  }, []);
  
  // Manejar cambio en selección de rango de tiempo
  const handleTimeRangeChange = (e) => {
    const newTimeRange = e.target.value;
    setTempFilters(prev => ({
      ...prev,
      timeRange: newTimeRange
    }));
    
    // Si no es rango personalizado, actualizar fechas automáticamente
    if (newTimeRange !== 'custom') {
      const dateRange = calculateDateRange(newTimeRange);
      setTempFilters(prev => ({
        ...prev,
        startDate: dateRange.startDate,
        endDate: dateRange.endDate
      }));
    }
  };
  
  // Manejar cambios en filtros temporales
  const handleTempFilterChange = (e) => {
    const { name, value } = e.target;
    
    if (name === 'status') {
      setTempFilters(prev => ({
        ...prev,
        status: value === 'all' ? ['approved', 'pending', 'rejected'] : [value]
      }));
    } else {
      setTempFilters(prev => ({
        ...prev,
        [name]: value
      }));
    }
  };
  
  // Aplicar filtros
  const applyFilters = () => {
    setFilters(tempFilters);
  };
  
  // Limpiar filtros
  const clearFilters = () => {
    const dateRange = calculateDateRange('thisMonth');
    const defaultFilters = {
      timeRange: 'thisMonth',
      platformId: '',
      status: ['approved', 'pending'],
      startDate: dateRange.startDate,
      endDate: dateRange.endDate,
      conversionFilter: 'all'
    };
    
    setTempFilters(defaultFilters);
    setFilters(defaultFilters);
  };

  // Configuración del gráfico de barras
  const barChartData = (() => {
    const dateRange = calculateDateRange(
      filters.timeRange, 
      filters.startDate, 
      filters.endDate
    );

    const salesChartDateRange = clampDateRangeEndToToday(dateRange);
    const months = [];
    const startDate = new Date(dateRange.startDate);
    const salesEndDate = salesChartDateRange
      ? new Date(salesChartDateRange.endDate)
      : null;
    const latestExecutionMonthKey = (stats?.salesByExecutionMonth || [])
      .map(item => item.monthKey)
      .filter(Boolean)
      .sort()
      .at(-1);
    const latestExecutionMonthDate = latestExecutionMonthKey
      ? monthKeyToDate(latestExecutionMonthKey)
      : null;

    let axisEndDate = salesEndDate
      ? new Date(salesEndDate.getFullYear(), salesEndDate.getMonth(), 1)
      : null;

    if (latestExecutionMonthDate && (!axisEndDate || latestExecutionMonthDate > axisEndDate)) {
      axisEndDate = latestExecutionMonthDate;
    }

    let currentDate = new Date(startDate.getFullYear(), startDate.getMonth(), 1);
    while (axisEndDate && currentDate <= axisEndDate) {
      months.push(formatMonthLabel(currentDate));
      currentDate.setMonth(currentDate.getMonth() + 1);
    }

    const salesByMonthMap = {};
    stats?.salesByMonth?.forEach(item => {
      salesByMonthMap[item.monthKey || item.month] = parseFloat(item.totalCommissionEUR) || 0;
    });

    const salesByExecutionMonthMap = {};
    stats?.salesByExecutionMonth?.forEach(item => {
      salesByExecutionMonthMap[item.monthKey || item.month] = parseFloat(item.totalCommissionEUR) || 0;
    });

    return {
      labels: months,
      datasets: [
        {
          label: 'Comisiones por fecha de venta',
          data: months.map((_, index) => {
            const monthDate = new Date(startDate.getFullYear(), startDate.getMonth() + index, 1);
            return salesByMonthMap[getMonthKey(monthDate)] || 0;
          }),
          backgroundColor: chartPalette.primaryFill,
          borderColor: chartPalette.primaryLine,
          borderWidth: 1,
          yAxisID: 'y'
        },
        {
          label: 'Comisiones por fecha de ejecución',
          data: months.map((_, index) => {
            const monthDate = new Date(startDate.getFullYear(), startDate.getMonth() + index, 1);
            return salesByExecutionMonthMap[getMonthKey(monthDate)] || 0;
          }),
          backgroundColor: chartPalette.secondaryFill,
          borderColor: chartPalette.secondaryLine,
          borderWidth: 1,
          yAxisID: 'y'
        }
      ]
    };
  })();

  const barChartOptions = useMemo(() => ({
    responsive: true,
    maintainAspectRatio: false,
    plugins: {
      legend: {
        position: 'top',
      },
      title: {
        display: true,
        text: 'Comisiones por Mes'
      },
      tooltip: {
        callbacks: {
          label: function(context) {
            return `${context.dataset.label}: ${formatEUR(context.raw)}`;
          }
        }
      }
    },
    scales: {
      y: {
        beginAtZero: true,
        title: {
          display: true,
          text: 'Comisión (€)'
        },
        ticks: {
          callback: function(value) {
            return formatEUR(value);
          }
        }
      }
    }
  }), []);

  // Preparar datos para el gráfico de pastel (comisiones por plataforma)
  const pieChartData = {
    labels: stats?.salesByPlatform?.map(item => item.platformName) || [],
    datasets: [
      {
        label: 'Comisiones por Plataforma',
        data: stats?.salesByPlatform?.map(item => parseFloat(item.totalCommissionEUR)) || [],
        backgroundColor: stats?.salesByPlatform?.map(item => {
          const platform = platforms.find(p => p.id === item.platformId);
          return platform?.color ? `${platform.color}80` : '#80808080'; // 80 añade 50% de opacidad
        }) || [],
        borderColor: stats?.salesByPlatform?.map(item => {
          const platform = platforms.find(p => p.id === item.platformId);
          return platform?.color || '#808080';
        }) || [],
        borderWidth: 1,
      },
    ],
  };

  const pieChartOptions = {
    responsive: true,
    plugins: {
      legend: {
        position: 'right',
      },
      title: {
        display: true,
        text: 'Distribución de Comisiones por Plataforma'
      }
    }
  };

  // Preparar datos para el gráfico de ventas por estado
  const statusPieChartData = {
    labels: ['Aprobadas', 'Pendientes', 'Rechazadas'],
    datasets: [
      {
        label: 'Ventas por Estado',
        data: [
          stats?.totalStats?.approvedSales || 0,
          stats?.totalStats?.pendingSales || 0,
          stats?.totalStats?.rejectedSales || 0
        ],
        backgroundColor: [
          chartPalette.successFill,
          chartPalette.warningFill,
          chartPalette.errorFill
        ],
        borderColor: [
          chartPalette.successLine,
          chartPalette.warningLine,
          chartPalette.errorLine
        ],
        borderWidth: 1,
      },
    ],
  };

  const statusPieChartOptions = {
    responsive: true,
    plugins: {
      legend: {
        position: 'right',
      },
      title: {
        display: true,
        text: 'Distribución de Ventas por Estado'
      },
      tooltip: {
        callbacks: {
          label: function(context) {
            const total = context.dataset.data.reduce((a, b) => a + b, 0);
            const percentage = ((context.raw / total) * 100).toFixed(1);
            return `${context.label}: ${context.raw} (${percentage}%)`;
          }
        }
      }
    }
  };

  // Preparar datos para el gráfico de ventas por plataforma
  const salesPieChartData = {
    labels: stats?.salesByPlatform?.map(item => item.platformName) || [],
    datasets: [
      {
        label: 'Ventas por Plataforma',
        data: stats?.salesByPlatform?.map(item => item.totalSales) || [],
        backgroundColor: stats?.salesByPlatform?.map(item => {
          const platform = platforms.find(p => p.id === item.platformId);
          return platform?.color ? `${platform.color}B3` : '#808080B3';
        }) || [],
        borderColor: stats?.salesByPlatform?.map(item => {
          const platform = platforms.find(p => p.id === item.platformId);
          return platform?.color || '#808080';
        }) || [],
        borderWidth: 1,
      },
    ],
  };

  const salesPieChartOptions = {
    responsive: true,
    plugins: {
      legend: {
        position: 'right',
      },
      title: {
        display: true,
        text: 'Distribución de Ventas por Plataforma'
      }
    }
  };

  // Función para generar un rango completo de días entre dos fechas
  const generateDateRange = (startDate, endDate) => {
    const dates = [];
    const currentDate = new Date(startDate);
    const lastDate = new Date(endDate);
    
    while (currentDate <= lastDate) {
      dates.push(new Date(currentDate));
      currentDate.setDate(currentDate.getDate() + 1);
    }
    
    return dates;
  };

  const formatDayLabel = (date, includeYear = false) => {
    return date.toLocaleDateString('es-ES', {
      day: 'numeric',
      month: 'short',
      ...(includeYear ? { year: '2-digit' } : {})
    });
  };

  // Preparar datos para el gráfico temporal de comisiones asegurando todos los días
  const dailyStackedBarChartData = (() => {
    const selectedDateRange = calculateDateRange(
      filters.timeRange, 
      filters.startDate, 
      filters.endDate
    );
    const dateRange = clampDateRangeEndToToday(selectedDateRange);

    if (!dateRange) {
      return {
        labels: [],
        datasets: platforms.map(platform => ({
          label: platform.name,
          data: [],
          absoluteData: [],
          backgroundColor: platform.color ? `${platform.color}80` : '#80808080',
          borderColor: platform.color || '#808080',
          borderWidth: 1,
          stack: 'daily-stack'
        }))
      };
    }
    
    const allDays = generateDateRange(
      new Date(dateRange.startDate), 
      new Date(dateRange.endDate)
    );

    const includeYearInLabels =
      new Date(dateRange.startDate).getFullYear() !== new Date(dateRange.endDate).getFullYear();

    const dayEntries = allDays.map(date => ({
      key: formatDateKey(date),
      label: formatDayLabel(date, includeYearInLabels)
    }));
    
    const dailyTotalsMap = {};
    const dailyPlatformMap = {};

    if (stats?.platformCommissionsByDay) {
      stats.platformCommissionsByDay.forEach(item => {
        const date = new Date(item.day);
        const key = formatDateKey(date);

        if (!dailyTotalsMap[key]) {
          dailyTotalsMap[key] = 0;
        }

        if (!dailyPlatformMap[key]) {
          dailyPlatformMap[key] = {};
        }

        const totalCommission = parseFloat(item.totalCommissionEUR) || 0;
        dailyTotalsMap[key] += totalCommission;
        dailyPlatformMap[key][item.platformId] =
          (dailyPlatformMap[key][item.platformId] || 0) + totalCommission;
      });
    }

    // Generar los datasets para cada plataforma
    const platformDatasets = platforms.map(platform => {
      // Datos completos para la plataforma
      const platformAbsoluteData = dayEntries.map(({ key }) => {
        return dailyPlatformMap[key]?.[platform.id] || 0;
      });

      const platformData = dayEntries.map(({ key }) => {
        const totalForDay = dailyTotalsMap[key] || 0;
        const platformValue = dailyPlatformMap[key]?.[platform.id] || 0;
        return totalForDay > 0 ? (platformValue / totalForDay) * 100 : 0;
      });
      
      return {
        label: platform.name,
        data: platformData,
        absoluteData: platformAbsoluteData,
        backgroundColor: platform.color ? `${platform.color}80` : '#80808080',
        borderColor: platform.color || '#808080',
        borderWidth: 1,
        stack: 'daily-stack'
      };
    });
    
    return {
      labels: dayEntries.map(({ label }) => label),
      datasets: platformDatasets
    };
  })();
  

  const dailyStackedBarChartOptions = useMemo(() => ({
    responsive: true,
    maintainAspectRatio: false,
    interaction: {
      mode: 'index',
      intersect: false,
    },
    plugins: {
      legend: {
        position: 'top',
      },
      title: {
        display: true,
        text: 'Comisiones Diarias por Plataforma'
      },
      tooltip: {
        callbacks: {
          label: function(context) {
            const label = context.dataset.label || '';
            const value = context.parsed.y !== null ? context.parsed.y : 0;
            const absoluteValue = context.dataset.absoluteData?.[context.dataIndex] || 0;
            return `${label}: ${formatEUR(absoluteValue)} (${value.toFixed(1)}%)`;
          }
        }
      }
    },
    scales: {
      x: {
        stacked: true
      },
      y: {
        stacked: true,
        type: 'linear',
        display: true,
        position: 'left',
        title: {
          display: true,
          text: 'Porcentaje del Total Diario'
        },
        beginAtZero: true,
        max: 100,
        ticks: {
          callback: function(value) {
            return `${value}%`;
          }
        }
      }
    }
  }), []);

  const dailyStackedBarChartPlugins = useMemo(() => ([
    {
      id: 'dailyStackedBarLabels',
      afterDatasetsDraw(chart) {
        const { ctx } = chart;

        ctx.save();
        ctx.font = '10px sans-serif';
        ctx.textAlign = 'center';
        ctx.textBaseline = 'middle';

        chart.data.datasets.forEach((dataset, datasetIndex) => {
          const meta = chart.getDatasetMeta(datasetIndex);

          meta.data.forEach((element, index) => {
            const value = dataset.data[index];
            const absoluteValue = dataset.absoluteData?.[index] || 0;

            if (!value || !element) {
              return;
            }

            const height = Math.abs(element.base - element.y);
            if (height < 14) {
              return;
            }

            ctx.fillStyle = '#ffffff';
            const label = `${formatEUR(absoluteValue)} (${value.toFixed(1)}%)`;
            ctx.fillText(label, element.x, element.y + height / 2);
          });
        });

        ctx.restore();
      }
    }
  ]), []);

  const formatter = new Intl.NumberFormat('es-ES', {
    style: 'currency',
    currency: 'EUR'
  });

  const activeDateRange = calculateDateRange(
    filters.timeRange,
    filters.startDate,
    filters.endDate
  );

  const selectedPlatformName = filters.platformId
    ? platforms.find((platform) => String(platform.id) === String(filters.platformId))?.name || 'Plataforma seleccionada'
    : 'Todas las plataformas';

  const trackedStatuses = {
    approved: 'Aprobadas',
    pending: 'Pendientes',
    rejected: 'Rechazadas'
  };

  const selectedStatuses = filters.status?.length
    ? filters.status.map((status) => trackedStatuses[status] || status).join(' · ')
    : 'Todos los estados';

  const totalGeneratedCommission = stats?.salesByMonth?.reduce(
    (acc, item) => acc + parseFloat(item.totalCommissionEUR || 0),
    0
  ) || 0;

  const totalExecutionCommission = stats?.salesByExecutionMonth?.reduce(
    (acc, item) => acc + parseFloat(item.totalCommissionEUR || 0),
    0
  ) || 0;

  const totalSales = stats?.totalStats?.totalSales || 0;
  const approvedSales = stats?.totalStats?.approvedSales || 0;
  const pendingSales = stats?.totalStats?.pendingSales || 0;
  const rejectedSales = stats?.totalStats?.rejectedSales || 0;
  const approvalRate = totalSales > 0 ? `${((approvedSales / totalSales) * 100).toFixed(1)}%` : '0%';

  const summaryCards = [
    {
      label: 'Comisiones generadas',
      value: formatter.format(totalGeneratedCommission),
      note: 'Importe registrado por fecha de venta dentro del periodo activo.',
      accentClass: 'metric-accent'
    },
    {
      label: 'Comisiones a cobrar',
      value: formatter.format(totalExecutionCommission),
      note: 'Comision prevista por fecha de ejecucion para cerrar seguimiento.',
      accentClass: 'metric-accent'
    },
    {
      label: 'Ventas aprobadas',
      value: approvedSales,
      note: `${approvalRate} de aprobacion sobre ${totalSales} ventas totales.`,
      accentClass: 'text-success'
    },
    {
      label: 'Riesgo operativo',
      value: pendingSales + rejectedSales,
      note: `${pendingSales} pendientes y ${rejectedSales} canceladas para revisar.`,
      accentClass: pendingSales + rejectedSales > 0 ? 'text-warning' : 'metric-accent'
    }
  ];

  // Preparar datos para el gráfico de ventas por semana
  const stackedBarChartData = useMemo(() => {
    if (!stats?.platformCommissionsByDay) return null;

    const selectedDateRange = calculateDateRange(
      filters.timeRange,
      filters.startDate,
      filters.endDate
    );
    const dateRange = clampDateRangeEndToToday(selectedDateRange);

    if (!dateRange) {
      return {
        labels: [],
        datasets: platforms.map(platform => ({
          label: platform.name,
          data: [],
          backgroundColor: platform.color || '#808080',
          borderColor: platform.color || '#808080',
          borderWidth: 1,
          stack: 'stack0'
        }))
      };
    }

    const weeks = generateWeeks(dateRange.startDate, dateRange.endDate);

    // Crear un mapa para acceder rápidamente a las ventas por semana y plataforma
    const salesByWeekAndPlatform = {};
    stats.platformCommissionsByDay.forEach(item => {
      const week = weeks.find(w => isDayInWeek(item.day, w));
      
      if (week) {
        if (!salesByWeekAndPlatform[week.label]) {
          salesByWeekAndPlatform[week.label] = {};
        }
        
        if (!salesByWeekAndPlatform[week.label][item.platformId]) {
          salesByWeekAndPlatform[week.label][item.platformId] = 0;
        }
        
        salesByWeekAndPlatform[week.label][item.platformId] += item.totalCommissionEUR;
      }
    });

    // Generar los datasets para cada plataforma
    const platformDatasets = platforms.map(platform => {
      const platformData = weeks.map(week => {
        return salesByWeekAndPlatform[week.label]?.[platform.id] || 0;
      });
      
      return {
        label: platform.name,
        data: platformData,
        backgroundColor: platform.color || '#808080',
        borderColor: platform.color || '#808080',
        borderWidth: 1,
        stack: 'stack0'
      };
    });

    return {
      labels: weeks.map(w => w.label),
      datasets: platformDatasets
    };
  }, [stats, filters, platforms]);

  // Opciones para el gráfico de ventas por semana
  const stackedBarChartOptions = useMemo(() => ({
    responsive: true,
    maintainAspectRatio: false,
    plugins: {
      legend: {
        position: 'top',
      },
      title: {
        display: true,
        text: 'Ventas por Semana por Plataforma'
      },
      tooltip: {
        callbacks: {
          label: function(context) {
            let label = context.dataset.label || '';
            if (label) {
              label += ': ';
            }
            if (context.parsed.y !== null) {
              label += formatEUR(context.parsed.y);
            }
            return label;
          }
        }
      }
    },
    scales: {
      x: {
        stacked: true,
      },
      y: {
        stacked: true,
        beginAtZero: true,
        title: {
          display: true,
          text: 'Comisiones (€)'
        },
        ticks: {
          callback: function(value) {
            return formatEUR(value);
          }
        }
      }
    }
  }), []);

  // Preparar datos para el gráfico de conversiones
  const conversionChartData = useMemo(() => {
    if (!stats?.platformCommissionsByDay) return null;

    const selectedDateRange = calculateDateRange(
      filters.timeRange,
      filters.startDate,
      filters.endDate
    );
    const dateRange = clampDateRangeEndToToday(selectedDateRange);

    if (!dateRange) {
      return {
        labels: [],
        datasets: [
          {
            label: '% Conversiones',
            data: [],
            type: 'line',
            borderColor: chartPalette.secondaryLine,
            backgroundColor: 'rgba(198, 146, 20, 0.18)',
            borderWidth: 2,
            fill: false,
            yAxisID: 'y1',
            order: 0
          },
          ...platforms.map(platform => ({
            label: platform.name,
            data: [],
            backgroundColor: platform.color || chartPalette.fallbackFill,
            stack: 'stack0',
            order: 1
          }))
        ]
      };
    }

    const weeks = generateWeeks(dateRange.startDate, dateRange.endDate);

    // Mapear datos por semana y plataforma
    const weeklyData = weeks.map(week => {
      const weekSales = stats.platformCommissionsByDay.filter(sale => {
        return isDayInWeek(sale.day, week);
      });

      // Calcular comisiones por plataforma con conversión
      const platformCommissions = {};
      let totalCommission = 0;
      let commissionWithConversion = 0;

      weekSales.forEach(sale => {
        if (!platformCommissions[sale.platformId]) {
          platformCommissions[sale.platformId] = {
            platformName: sale.platformName,
            platformColor: sale.platformColor,
            commission: 0
          };
        }
        
        if (sale.gclid) {
          platformCommissions[sale.platformId].commission += sale.totalCommissionEUR;
          commissionWithConversion += sale.totalCommissionEUR;
        }
        totalCommission += sale.totalCommissionEUR;
      });

      return {
        week: week.label,
        platforms: Object.values(platformCommissions),
        conversionRate: totalCommission > 0 ? (commissionWithConversion / totalCommission) * 100 : 0
      };
    });

    // Dataset para la línea de porcentaje de conversión
    const lineDataset = {
      label: '% Conversiones',
      data: weeklyData.map(week => week.conversionRate),
      type: 'line',
      borderColor: chartPalette.secondaryLine,
      backgroundColor: 'rgba(198, 146, 20, 0.18)',
      borderWidth: 2,
      fill: false,
      yAxisID: 'y1',
      order: 0
    };

    // Crear datasets para las barras apiladas
    const barDatasets = platforms.map(platform => {
      return {
        label: platform.name,
        data: weeklyData.map(week => {
          const platformData = week.platforms.find(p => p.platformName === platform.name);
          return platformData ? platformData.commission : 0;
        }),
        backgroundColor: platform.color || chartPalette.fallbackFill,
        stack: 'stack0',
        order: 1
      };
    });

    return {
      labels: weeklyData.map(week => week.week),
      datasets: [lineDataset, ...barDatasets]
    };
  }, [stats, filters, platforms]);

  // Opciones para el gráfico de conversiones
  const conversionChartOptions = useMemo(() => ({
    responsive: true,
    maintainAspectRatio: false,
    plugins: {
      legend: {
        position: 'top',
      },
      tooltip: {
        callbacks: {
          label: function(context) {
            const dataset = context.dataset;
            if (dataset.type === 'line') {
              return `${dataset.label}: ${context.parsed.y.toFixed(1)}%`;
            }
            return `${dataset.label}: ${formatEUR(context.parsed.y)}`;
          }
        }
      }
    },
    scales: {
      x: {
        stacked: true,
      },
      y: {
        stacked: true,
        title: {
          display: true,
          text: 'Comisiones (€)'
        }
      },
      y1: {
        position: 'right',
        min: 0,
        max: 100,
        title: {
          display: true,
          text: '% Conversiones'
        },
        grid: {
          drawOnChartArea: false
        }
      }
    }
  }), []);

  if (loading && !stats) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <span className="loading loading-spinner loading-lg"></span>
      </div>
    );
  }

  if (error) {
    return (
      <div className="alert alert-error">
        <svg xmlns="http://www.w3.org/2000/svg" className="stroke-current shrink-0 h-6 w-6" fill="none" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M10 14l2-2m0 0l2-2m-2 2l-2-2m2 2l2 2m7-2a9 9 0 11-18 0 9 9 0 0118 0z" />
        </svg>
        <span>{error}</span>
      </div>
    );
  }

  return (
    <div className="space-y-8">
      <section className="dashboard-hero">
        <div className="dashboard-hero-grid">
          <div className="space-y-4">
            <p className="page-eyebrow">Panel operativo premium</p>
            <div className="page-header">
              <h1 className="page-title">Dashboard</h1>
              <p className="page-subtitle">
                Vista consolidada para controlar volumen, aprobacion y comisiones con una
                jerarquia mas clara y foco inmediato en el rendimiento del periodo.
              </p>
            </div>
          </div>

          <div className="context-chip-row">
            <span className="context-chip">
              <strong>Periodo</strong>
              <span>{activeDateRange.startDate} a {activeDateRange.endDate}</span>
            </span>
            <span className="context-chip">
              <strong>Plataforma</strong>
              <span>{selectedPlatformName}</span>
            </span>
            <span className="context-chip">
              <strong>Estado</strong>
              <span>{selectedStatuses}</span>
            </span>
            <span className="context-chip">
              <strong>Conversion</strong>
              <span>{filters.conversionFilter === 'all' ? 'Todas' : filters.conversionFilter}</span>
            </span>
          </div>
        </div>
      </section>

      <section className="metric-grid">
        {summaryCards.map((card) => (
          <article key={card.label} className="premium-panel metric-card">
            <span className="metric-label">{card.label}</span>
            <div className={`metric-value ${card.accentClass}`}>{card.value}</div>
            <p className="metric-note">{card.note}</p>
          </article>
        ))}
      </section>

      {/* Filtros */}
      <div className="premium-panel filter-panel">
        <div className="panel-header">
          <p className="panel-kicker">Ajustes de lectura</p>
          <h2 className="panel-title">Filtros del panel</h2>
          <p className="panel-subtitle">
            Refina el periodo y el alcance para analizar el rendimiento operativo sin perder contexto.
          </p>
        </div>
        <div>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <div className="form-control w-full">
              <label className="label">
                <span className="label-text font-medium">Rango de Tiempo</span>
              </label>
              <select
                className="select select-bordered w-full"
                value={tempFilters.timeRange}
                onChange={handleTimeRangeChange}
                name="timeRange"
              >
                <option value="lastWeek">Semana Anterior</option>
                <option value="lastMonth">Mes Anterior</option>
                <option value="lastYear">Año Anterior</option>
                <option value="last7days">Últimos 7 días</option>
                <option value="last30days">Últimos 30 días</option>
                <option value="last90days">Últimos 90 días</option>
                <option value="thisWeek">Semana Actual</option>
                <option value="thisMonth">Mes Actual</option>
                <option value="thisYear">Año Actual</option>
                <option value="custom">Rango Personalizado</option>
              </select>
            </div>

            {tempFilters.timeRange === 'custom' && (
              <>
                <div className="form-control w-full">
                  <label className="label">
                    <span className="label-text font-medium">Fecha Inicio</span>
                  </label>
                  <input
                    type="date"
                    className="input input-bordered w-full"
                    value={tempFilters.startDate}
                    onChange={(e) => setTempFilters(prev => ({
                      ...prev,
                      startDate: e.target.value
                    }))}
                    name="startDate"
                  />
                </div>

                <div className="form-control w-full">
                  <label className="label">
                    <span className="label-text font-medium">Fecha Fin</span>
                  </label>
                  <input
                    type="date"
                    className="input input-bordered w-full"
                    value={tempFilters.endDate}
                    onChange={(e) => setTempFilters(prev => ({
                      ...prev,
                      endDate: e.target.value
                    }))}
                    name="endDate"
                  />
                </div>
              </>
            )}

            <div className="form-control w-full">
              <label className="label">
                <span className="label-text font-medium">Plataforma</span>
              </label>
              <select
                className="select select-bordered w-full"
                value={tempFilters.platformId}
                onChange={handleTempFilterChange}
                name="platformId"
              >
                <option value="">Todas las plataformas</option>
                {platforms.map(platform => (
                  <option key={platform.id} value={platform.id}>
                    {platform.name}
                  </option>
                ))}
              </select>
            </div>

            <div className="form-control w-full">
              <label className="label">
                <span className="label-text font-medium">Estado</span>
              </label>
              <select
                className="select select-bordered w-full"
                value={tempFilters.status}
                onChange={(e) => {
                  const options = e.target.selectedOptions;
                  const values = Array.from(options).map(option => option.value);
                  setTempFilters(prev => ({
                    ...prev,
                    status: values.length > 0 ? values : ['approved', 'pending']
                  }));
                }}
                name="status"
                multiple
                size="3"
              >
                <option value="pending">Pendiente</option>
                <option value="approved">Aprobada</option>
                <option value="rejected">Rechazada</option>
              </select>
              <label className="label">
                <span className="label-text-alt">Mantén presionado Ctrl/Cmd para selección múltiple</span>
              </label>
            </div>

            <div className="form-control w-full">
              <label className="label">
                <span className="label-text font-medium">Filtro de Conversión</span>
              </label>
              <select
                className="select select-bordered w-full"
                value={tempFilters.conversionFilter}
                onChange={(e) => setTempFilters(prev => ({
                  ...prev,
                  conversionFilter: e.target.value
                }))}
                name="conversionFilter"
              >
                <option value="all">Todas las ventas</option>
                <option value="with_conversion">Con conversión</option>
                <option value="without_conversion">Sin conversión</option>
              </select>
            </div>
          </div>
          
          <div className="flex justify-end mt-4 gap-2">
            {loading && (
              <div className="flex items-center">
                <span className="loading loading-spinner loading-sm mr-2"></span>
                <span>Cargando...</span>
              </div>
            )}
            <button 
              className="btn btn-ghost"
              onClick={clearFilters}
            >
              Limpiar Filtros
            </button>
            <button 
              className="btn btn-primary"
              onClick={applyFilters}
              disabled={loading}
            >
              Aplicar Filtros
            </button>
          </div>
        </div>
      </div>

      {/* Resumen de Estadísticas */}
      <div className="stats shadow w-full">
        
      
        <div className="stat">
          <div className="stat-title">Comisiones Generadas</div>
          <div className="stat-value">
            {formatter.format(stats?.salesByMonth?.reduce((acc, item) => acc + parseFloat(item.totalCommissionEUR || 0), 0) || 0)}
          </div>
          <div className="stat-desc">Según fecha de venta</div>
        </div>
        
        <div className="stat">
          <div className="stat-title">Comisiones a Cobrar</div>
          <div className="stat-value">
            {formatter.format(stats?.salesByExecutionMonth?.reduce((acc, item) => acc + parseFloat(item.totalCommissionEUR || 0), 0) || 0)}
          </div>
          <div className="stat-desc">Según fecha de ejecución</div>
        </div>

        <div className="stat">
          <div className="stat-title">Total Ventas</div>
          <div className="stat-value">{stats?.totalStats?.totalSales || 0}</div>
        </div>

        <div className="stat">
          <div className="stat-title">Reservas Aprobadas</div>
          <div className="stat-value text-success">{stats?.totalStats?.approvedSales || 0}</div>
        </div>

        <div className="stat">
          <div className="stat-title">Reservas Pendientes</div>
          <div className="stat-value text-warning">{stats?.totalStats?.pendingSales || 0}</div>
        </div>

        <div className="stat">
          <div className="stat-title">Reservas Canceladas</div>
          <div className="stat-value text-error">{stats?.totalStats?.rejectedSales || 0}</div>
        </div>
      </div>

      {/* Gráficos */}
      <div className="grid grid-cols-1 gap-6">
        <div className="card bg-base-100 shadow-xl">
          <div className="card-body">
            <h2 className="card-title">Comisiones por Mes</h2>
            <div className="h-[600px] w-full overflow-x-auto">
              <Bar data={barChartData} options={barChartOptions} />
            </div>
          </div>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
          <div className="card bg-base-100 shadow-xl">
            <div className="card-body">
              <h2 className="card-title text-lg">Ventas por Estado</h2>
              <div className="h-[300px]">
                <Pie data={statusPieChartData} options={statusPieChartOptions} />
              </div>
            </div>
          </div>

          <div className="card bg-base-100 shadow-xl">
            <div className="card-body">
              <h2 className="card-title text-lg">Comisiones por Plataforma</h2>
              <div className="h-[300px]">
                <Pie data={pieChartData} options={pieChartOptions} />
              </div>
            </div>
          </div>

          <div className="card bg-base-100 shadow-xl">
            <div className="card-body">
              <h2 className="card-title text-lg">Ventas por Plataforma</h2>
              <div className="h-[300px]">
                <Pie data={salesPieChartData} options={salesPieChartOptions} />
              </div>
            </div>
          </div>
        </div>

        <div className="card bg-base-100 shadow-xl">
          <div className="card-body">
            <h2 className="card-title">Ventas por Semana por Plataforma</h2>
            <div className="h-[600px] w-full overflow-x-auto">
              <Bar data={stackedBarChartData} options={stackedBarChartOptions} />
            </div>
          </div>
        </div>

        {/* Gráfico de Conversiones */}
        <div className="card bg-base-100 shadow-xl">
          <div className="card-body">
            <h2 className="card-title">Conversiones por Semana</h2>
            <div className="h-[600px] w-full overflow-x-auto">
              {conversionChartData && (
                <Bar data={conversionChartData} options={conversionChartOptions} />
              )}
            </div>
          </div>
        </div>

        <div className="card bg-base-100 shadow-xl">
          <div className="card-body">
            <h2 className="card-title">Comisiones Diarias</h2>
            <div className="h-[600px] w-full overflow-x-auto">
              <Bar
                data={dailyStackedBarChartData}
                options={dailyStackedBarChartOptions}
                plugins={dailyStackedBarChartPlugins}
              />
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default DashboardScreen; 
